import { ComponentType } from 'react';

export type ProviderWithOrWithoutProps<Props = any> =
  | ComponentType<Props>
  | [ComponentType<Props>, Props];

export type FlattenProvidersProps = {
  providers: ProviderWithOrWithoutProps[];
  children: React.ReactNode;
};

/**
 * This component allows to write a traditional "Christmas tree" like structure
 * into a flat list.
 *
 * Example:
 * ```
 * <Provider1>
 *   <Provider2>
 *     <Provider3>
 *       ...
 *     </Provider3>
 *   </Provider2>
 * </Provider1>
 * ```
 *
 * Can be written like:
 * ```
 * <FlattenProviders providers=[Provider1, Provider2, Provider3]>
 *   ...
 * </FlattenProviders>
 * ```
 *
 * @param children - component children
 * @param providers - providers to flatten
 * @constructor
 */
export const FlattenProviders = ({
  children,
  providers,
}: FlattenProvidersProps) => {
  // Tree is created from bottom to top.
  const reversedProviders = [...providers].reverse();

  return (
    <>
      {reversedProviders.reduce((reducedChildren, provider) => {
        const [ProviderComponent, props] = Array.isArray(provider)
          ? provider
          : ([provider, {}] as any);

        return (
          <ProviderComponent {...props}>{reducedChildren}</ProviderComponent>
        );
      }, children)}
    </>
  );
};
